import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  MdStar,
  MdStarBorder,
  MdFilterList,
  MdClose,
  MdArrowBack,
  MdArrowForward,
  MdFavorite,
  MdFavoriteBorder,
} from 'react-icons/md';
import { MdGridView, MdViewList } from 'react-icons/md';
import Breadcrumb from '../components/shared/Breadcrumb';
import Carousel from '../components/shared/Carousel';
import AddToCartButton from '../components/shared/AddToCartButton';
import image61 from '../assets/image61.png';
import image62 from '../assets/image62.png';
import image63 from '../assets/image63.png';
import image66 from '../assets/image66.png';
import image67 from '../assets/image67.png';
import { useCartStore } from '../store/cartStore';
import { listSkus, Sku } from '../services/skusApi';
import { listBackendCategories, categoryToFrontend } from '../services/categoriesApi';
import { getSuggestedReorder } from '../services/ordersApi';
import { listPromotions, Promotion } from '../services/promotionsApi';
import { getFavoriteSkuIds, toggleFavoriteSku } from '../utils/favoritesStore';
import type { Category } from '../types';
import '../styles/CategoriesPage.css';

const NEW_WINDOW_DAYS = 30;

type QuickFilter = 'all' | 'favorites' | 'previously-ordered' | 'deals' | 'new';

const QUICK_FILTERS: { label: string; value: QuickFilter }[] = [
  { label: 'All Products', value: 'all' },
  { label: 'Favorites', value: 'favorites' },
  { label: 'Previously Ordered', value: 'previously-ordered' },
  { label: 'Deals', value: 'deals' },
  { label: 'New', value: 'new' },
];

/* ------------------------------------------------------------------ */
/*  Static Data                                                        */
/* ------------------------------------------------------------------ */

interface CategoryProduct {
  id: number;
  name: string;
  priceLabel: string;
  originalPrice: string | null;
  inStock: boolean;
  rating: number;
  brand: string;
  category: string;
  price: number;
  size: string;
  isBestSeller: boolean;
  badge: 'new' | 'sale' | null;
  isPreviouslyOrdered: boolean;
  isNew: boolean;
  isDeal: boolean;
}

const BRANDS = ['cannabis', 'Marijuana', 'cannabinoid', 'otherbrand'];

const SIZES = ['2" & Under', '2.5"', '5.0"', '6" x 6.5"', '4 x 1.5'];

function skuHasActiveDeal(sku: Sku, promotions: Promotion[]): boolean {
  const now = Date.now();
  return promotions.some((p) => {
    if (!p.is_active) return false;
    if (new Date(p.starts_at).getTime() > now || new Date(p.ends_at).getTime() < now) return false;
    if (p.scope === 'all') return true;
    if (p.scope === 'category') return p.category_id != null && p.category_id === sku.category_id;
    if (p.scope === 'sku') return p.sku_id != null && p.sku_id === sku.id;
    return false;
  });
}

/** Map a backend SKU into the local CategoryProduct shape this page renders.
 *  Backend has no brand/rating/size, so those default to empty/neutral.
 *  VP-CUST-002: uses the real per-customer tier price (customer_price) when
 *  present, showing the list price struck through when it differs — same
 *  pattern as skuToProduct in services/skusApi.ts. */
function skuToCategoryProduct(
  sku: Sku,
  previouslyOrderedSkuIds: Set<number>,
  promotions: Promotion[],
): CategoryProduct {
  const listPrice = sku.list_price ?? sku.unit_cost ?? 0;
  const price = sku.customer_price ?? listPrice;
  const isNew = (Date.now() - new Date(sku.created_at).getTime()) < NEW_WINDOW_DAYS * 86_400_000;
  return {
    id: sku.id,
    name: sku.product_name,
    priceLabel: `$${price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    originalPrice: listPrice !== price
      ? `$${listPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : null,
    inStock: sku.total_stock > 0,
    rating: 0,
    brand: '',
    category: sku.category_name ?? '',
    price,
    size: '',
    isBestSeller: false,
    badge: listPrice !== price ? 'sale' : (isNew ? 'new' : null),
    isPreviouslyOrdered: previouslyOrderedSkuIds.has(sku.id),
    isNew,
    isDeal: skuHasActiveDeal(sku, promotions),
  };
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const CategoriesPage: React.FC = () => {
  const navigate = useNavigate();
  const addToCart = useCartStore((s) => s.addItem);
  const [searchParams] = useSearchParams();
  const urlSearch = searchParams.get('search') || '';
  const urlCategory = searchParams.get('category') || '';

  /* -- live catalog data (API-driven) -- */
  const [rawSkus, setRawSkus] = useState<Sku[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const categoryNames = useMemo(() => categories.map((c) => c.name), [categories]);
  const [loading, setLoading] = useState(true);

  /* -- quick-filter data (VP-CUST-002): previously-ordered SKUs (from the
     suggested-reorder endpoint, since the orders-list summary only carries
     an item_count) and active promotions (for "Deals"). Favorites are
     client-side/localStorage — see utils/favoritesStore.ts. -- */
  const [previouslyOrderedSkuIds, setPreviouslyOrderedSkuIds] = useState<Set<number>>(new Set());
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [favoriteVersion, setFavoriteVersion] = useState(0);
  const [quickFilter, setQuickFilter] = useState<QuickFilter>('all');

  const products = useMemo(
    () => rawSkus.map((s) => skuToCategoryProduct(s, previouslyOrderedSkuIds, promotions)),
    [rawSkus, previouslyOrderedSkuIds, promotions],
  );

  /* -- filter state -- */
  const [selectedCategory, setSelectedCategory] = useState('All Categories');
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  const [priceMin, setPriceMin] = useState('');
  const [priceMax, setPriceMax] = useState('');
  const [selectedRating, setSelectedRating] = useState(0);
  const [selectedSizes, setSelectedSizes] = useState<string[]>([]);

  /* -- toolbar state -- */
  const [itemsPerPage, setItemsPerPage] = useState(24);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sortBy, setSortBy] = useState('default');
  const [currentPage, setCurrentPage] = useState(1);

  /* -- mobile sidebar -- */
  const [sidebarOpen, setSidebarOpen] = useState(false);

  /* -- category tree built from live categories -- */
  const categoryTree = useMemo(() => ['All Categories', ...categoryNames], [categoryNames]);

  /* -- fetch categories + products from the backend on mount -- */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [cats, skuPage] = await Promise.all([
          listBackendCategories().then((list) => list.map(categoryToFrontend)),
          listSkus({ limit: 100 }),
        ]);
        if (cancelled) return;
        setCategories(cats);
        setRawSkus(skuPage.data);
      } catch {
        if (!cancelled) {
          setCategories([]);
          setRawSkus([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    // "Previously Ordered" filter data. customerId is ignored server-side
    // for the Customer role (always resolved to the caller), so 'me' works.
    getSuggestedReorder('me')
      .then((lines) => setPreviouslyOrderedSkuIds(new Set(lines.map((l) => l.sku_id))))
      .catch(() => setPreviouslyOrderedSkuIds(new Set()));
    // "Deals" filter data — active promotions, matched by scope.
    listPromotions()
      .then(setPromotions)
      .catch(() => setPromotions([]));
    return () => {
      cancelled = true;
    };
  }, []);

  /* -- preselect category from the ?category= URL param once categories load.
     Links may pass either the raw name or a slug (e.g. "pre-rolls"), so match
     both forms. -- */
  useEffect(() => {
    if (!urlCategory || categoryNames.length === 0) return;
    const target = urlCategory.toLowerCase();
    const slugify = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    const match = categoryNames.find(
      (name) => name.toLowerCase() === target || slugify(name) === target,
    );
    if (match) setSelectedCategory(match);
  }, [urlCategory, categoryNames]);

  /* -- reset all filters -- */
  const resetFilters = () => {
    setSelectedCategory('All Categories');
    setSelectedBrands([]);
    setPriceMin('');
    setPriceMax('');
    setSelectedRating(0);
    setSelectedSizes([]);
    setQuickFilter('all');
    setCurrentPage(1);
  };

  const handleFavoriteToggle = (id: number) => {
    toggleFavoriteSku(id);
    setFavoriteVersion((v) => v + 1);
  };

  /* -- toggle brand -- */
  const toggleBrand = (brand: string) => {
    setSelectedBrands((prev) =>
      prev.includes(brand) ? prev.filter((b) => b !== brand) : [...prev, brand],
    );
    setCurrentPage(1);
  };

  /* -- toggle size -- */
  const toggleSize = (size: string) => {
    setSelectedSizes((prev) =>
      prev.includes(size) ? prev.filter((s) => s !== size) : [...prev, size],
    );
    setCurrentPage(1);
  };

  /* ---------------------------------------------------------------- */
  /*  Filtered + Sorted products                                       */
  /* ---------------------------------------------------------------- */
  const filteredProducts = useMemo(() => {
    let results = [...products];

    // Search (from the ?search= URL param, e.g. navbar search)
    if (urlSearch.trim()) {
      const q = urlSearch.trim().toLowerCase();
      results = results.filter((p) => p.name.toLowerCase().includes(q));
    }

    // Category
    if (selectedCategory !== 'All Categories') {
      results = results.filter((p) => p.category === selectedCategory);
    }

    // Brands
    if (selectedBrands.length > 0) {
      results = results.filter((p) => selectedBrands.includes(p.brand));
    }

    // Price
    const min = parseFloat(priceMin);
    const max = parseFloat(priceMax);
    if (!isNaN(min)) results = results.filter((p) => p.price >= min);
    if (!isNaN(max)) results = results.filter((p) => p.price <= max);

    // Rating
    if (selectedRating > 0) {
      results = results.filter((p) => p.rating >= selectedRating);
    }

    // Sizes
    if (selectedSizes.length > 0) {
      results = results.filter((p) => selectedSizes.includes(p.size));
    }

    // Quick filters (VP-CUST-002)
    switch (quickFilter) {
      case 'favorites': {
        const favIds = getFavoriteSkuIds();
        results = results.filter((p) => favIds.has(String(p.id)));
        break;
      }
      case 'previously-ordered':
        results = results.filter((p) => p.isPreviouslyOrdered);
        break;
      case 'deals':
        results = results.filter((p) => p.isDeal);
        break;
      case 'new':
        results = results.filter((p) => p.isNew);
        break;
      default:
        break;
    }

    // Sort
    switch (sortBy) {
      case 'price-low':
        results.sort((a, b) => a.price - b.price);
        break;
      case 'price-high':
        results.sort((a, b) => b.price - a.price);
        break;
      case 'rating':
        results.sort((a, b) => b.rating - a.rating);
        break;
      default:
        break;
    }

    return results;
    // favoriteVersion forces a re-filter after a favorite toggle
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, urlSearch, selectedCategory, selectedBrands, priceMin, priceMax, selectedRating, selectedSizes, sortBy, quickFilter, favoriteVersion]);

  /* -- pagination -- */
  const totalProducts = filteredProducts.length;
  const totalPages = Math.max(1, Math.ceil(totalProducts / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalProducts);
  const paginatedProducts = filteredProducts.slice(startIndex, endIndex);

  /* -- best sellers -- */
  const bestSellers = products.filter((p) => p.isBestSeller);
  void bestSellers;

  /* -- page numbers array -- */
  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

  /* ---------------------------------------------------------------- */
  /*  Helpers                                                          */
  /* ---------------------------------------------------------------- */

  const renderStars = (count: number) =>
    Array.from({ length: 5 }, (_, i) =>
      i < count ? (
        <MdStar key={i} className="categories__rating-star" />
      ) : (
        <MdStarBorder key={i} className="categories__rating-star categories__rating-star--empty" />
      ),
    );

  /* ---------------------------------------------------------------- */
  /*  Render                                                           */
  /* ---------------------------------------------------------------- */

  // Popular categories derived from the live catalog: real categories ranked
  // by product count (productCount = backend sku_count).
  const popularCategories = useMemo(
    () =>
      [...categories]
        .sort((a, b) => b.productCount - a.productCount)
        .slice(0, 10)
        .map((c) => ({ name: c.name, items: c.productCount })),
    [categories],
  );

  return (
    <>
      {/* ===== Breadcrumb Navigator ===== */}
      <Breadcrumb extra={selectedCategory !== 'All Categories' ? selectedCategory : undefined} />

      {/* ===== Promo Carousel ===== */}
      <section className="categories-carousel">
        <Carousel
          slides={[
            { id: 1, image: image62, tag: 'NEW ARRIVALS', title: 'Premium Cigarillos Collection', subtitle: 'Hand-picked from top distributors — now available at wholesale prices.', cta: { label: 'Shop Collection' }, bgColor: '#1A1040' },
            { id: 2, image: image61, tag: 'BEST SELLERS', title: 'Beverages & Drinks on Sale', subtitle: 'Stock up on trending drinks with exclusive partner discounts this week.', cta: { label: 'View Deals' }, bgColor: '#2D1A0E' },
            { id: 3, image: image63, tag: 'ECO-FRIENDLY', title: 'Natural & Organic Range', subtitle: 'Sustainable products your customers are asking for — delivered fast.', cta: { label: 'Explore Now' }, bgColor: '#0A2E1A' },
            { id: 4, image: image66, tag: 'TRENDING', title: 'New Packaging & Containers', subtitle: 'Premium retail containers in every style — elevate your shelf appeal.', cta: { label: 'Browse Range' }, bgColor: '#1A2540' },
          ]}
          autoPlay
          interval={4500}
        />
      </section>

      {/* ===== Popular Categories ===== */}
      {popularCategories.length > 0 && (
      <section className="popular-cats">
        <div className="popular-cats__container">
          <h2 className="popular-cats__title">POPULAR CATEGORIES</h2>
          <div className="popular-cats__grid">
            {popularCategories.map((cat, i) => (
              <button
                key={i}
                className="popular-cats__item"
                onClick={() => {
                  setSelectedCategory(cat.name);
                  setCurrentPage(1);
                }}
              >
                <div className="popular-cats__text">
                  <span className="popular-cats__name">{cat.name}</span>
                  <span className="popular-cats__count">{cat.items} Items</span>
                </div>
                <div className="popular-cats__thumb">
                  <img src={image67} alt={cat.name} />
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* ===== Mobile Filter Button ===== */}
      <button
        className="categories__mobile-filter-btn"
        onClick={() => setSidebarOpen(true)}
      >
        <MdFilterList /> Filters
      </button>

      {/* ===== Main Layout ===== */}
      <div className="categories">
        {/* Overlay */}
        <div
          className={`categories__sidebar-overlay${
            sidebarOpen ? ' categories__sidebar-overlay--visible' : ''
          }`}
          onClick={() => setSidebarOpen(false)}
        />

        {/* ---- Sidebar ---- */}
        <aside
          className={`categories__sidebar${sidebarOpen ? ' categories__sidebar--open' : ''}`}
        >
          {/* Mobile close */}
          <button
            className="categories__sidebar-close"
            onClick={() => setSidebarOpen(false)}
            style={{ display: sidebarOpen ? 'flex' : 'none' }}
          >
            <MdClose />
          </button>

          {/* Quick Filters (VP-CUST-002) */}
          <div className="categories__filter-section" style={{ marginTop: 0 }}>
            <h4 className="categories__filter-title">Quick Filters</h4>
            <div
              role="tablist"
              aria-label="Catalog quick filters"
              style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}
            >
              {QUICK_FILTERS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  role="tab"
                  aria-selected={quickFilter === opt.value}
                  onClick={() => {
                    setQuickFilter(opt.value);
                    setCurrentPage(1);
                  }}
                  style={{
                    padding: '6px 14px',
                    borderRadius: 999,
                    border: quickFilter === opt.value ? '1px solid #2B7FFF' : '1px solid #D1D5DB',
                    background: quickFilter === opt.value ? '#EFF6FF' : '#fff',
                    color: quickFilter === opt.value ? '#1D4ED8' : '#374151',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Category Tree */}
          <div className="categories__sidebar-header">
            <h3 className="categories__sidebar-title">Categories</h3>
            <button className="categories__reset" onClick={resetFilters}>
              Reset All
            </button>
          </div>

          <ul className="categories__tree">
            {categoryTree.map((cat) => (
              <li key={cat}>
                <button
                  className={`categories__tree-item${
                    cat === 'All Categories' ? ' categories__tree-item--parent' : ''
                  }${selectedCategory === cat ? ' categories__tree-item--active' : ''}`}
                  onClick={() => {
                    setSelectedCategory(cat);
                    setCurrentPage(1);
                    setSidebarOpen(false);
                  }}
                >
                  {cat}
                </button>
              </li>
            ))}
          </ul>

          {/* By Brands */}
          <div className="categories__filter-section">
            <h4 className="categories__filter-title">By Brands</h4>
            <div className="categories__checkbox-group">
              {BRANDS.map((brand) => (
                <label key={brand} className="categories__checkbox-label">
                  <input
                    type="checkbox"
                    className="categories__checkbox"
                    checked={selectedBrands.includes(brand)}
                    onChange={() => toggleBrand(brand)}
                  />
                  {brand}
                </label>
              ))}
            </div>
          </div>

          {/* By Price */}
          <div className="categories__filter-section">
            <h4 className="categories__filter-title">By Price</h4>
            <div className="categories__price-row">
              <div className="categories__price-input-wrap">
                <span className="categories__price-prefix">$</span>
                <input
                  type="number"
                  className="categories__price-input"
                  placeholder="Min"
                  value={priceMin}
                  onChange={(e) => {
                    setPriceMin(e.target.value);
                    setCurrentPage(1);
                  }}
                />
              </div>
              <span className="categories__price-sep">-</span>
              <div className="categories__price-input-wrap">
                <span className="categories__price-prefix">$</span>
                <input
                  type="number"
                  className="categories__price-input"
                  placeholder="Max"
                  value={priceMax}
                  onChange={(e) => {
                    setPriceMax(e.target.value);
                    setCurrentPage(1);
                  }}
                />
              </div>
            </div>
          </div>

          {/* By Rating */}
          <div className="categories__filter-section">
            <h4 className="categories__filter-title">By Rating</h4>
            <div className="categories__rating-group">
              {[5, 4, 3, 2, 1].map((star) => (
                <button
                  key={star}
                  className={`categories__rating-row${
                    selectedRating === star ? ' categories__rating-row--active' : ''
                  }`}
                  onClick={() => {
                    setSelectedRating(selectedRating === star ? 0 : star);
                    setCurrentPage(1);
                  }}
                >
                  {renderStars(star)}
                  <span className="categories__rating-label">& Up</span>
                </button>
              ))}
            </div>
          </div>

          {/* By Roll Size */}
          <div className="categories__filter-section">
            <h4 className="categories__filter-title">By Roll Size</h4>
            <div className="categories__size-group">
              {SIZES.map((size) => (
                <button
                  key={size}
                  className={`categories__size-pill${
                    selectedSizes.includes(size) ? ' categories__size-pill--active' : ''
                  }`}
                  onClick={() => toggleSize(size)}
                >
                  {size}
                </button>
              ))}
            </div>
          </div>
        </aside>

        {/* ---- Content ---- */}
        <div className="categories__content">
          {/* Toolbar */}
          <div className="categories__toolbar">
            <span className="categories__result-count">
              {totalProducts === 0
                ? '0 results'
                : `${startIndex + 1} - ${endIndex} of ${totalProducts} results`}
            </span>

            <div className="categories__toolbar-spacer" />

            <div className="categories__toolbar-group">
              <span className="categories__toolbar-label">Show</span>
              <select
                className="categories__toolbar-select"
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value={24}>24</option>
                <option value={48}>48</option>
                <option value={72}>72</option>
              </select>
            </div>

            <div className="categories__toolbar-group">
              <button
                className={`categories__view-btn${
                  viewMode === 'grid' ? ' categories__view-btn--active' : ''
                }`}
                onClick={() => setViewMode('grid')}
                aria-label="Grid view"
              >
                <MdGridView />
              </button>
              <button
                className={`categories__view-btn${
                  viewMode === 'list' ? ' categories__view-btn--active' : ''
                }`}
                onClick={() => setViewMode('list')}
                aria-label="List view"
              >
                <MdViewList />
              </button>
            </div>

            <div className="categories__toolbar-group">
              <span className="categories__toolbar-label">Sort</span>
              <select
                className="categories__toolbar-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="default">Default</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
                <option value="rating">Top Rated</option>
              </select>
            </div>

            <button className="categories__shop-all" onClick={resetFilters}>
              Shop All
            </button>
          </div>

          {/* Product Grid */}
          <div
            className={`categories__grid${viewMode === 'list' ? ' categories__grid--list' : ''}`}
          >
            {paginatedProducts.map((product, idx) => (
              <React.Fragment key={product.id}>
                {/* Carousel banner after the 8th product */}
                {idx === 8 && viewMode === 'grid' && (
                  <div style={{ gridColumn: '1 / -1' }}>
                    <Carousel
                      slides={[
                        { id: 1, image: image62, tag: 'WHOLESALE DEALS', title: 'Future-Proof Your Inventory', subtitle: 'Explore premium selections and lock in wholesale pricing today.', cta: { label: 'Shop Now' }, bgColor: '#1A1040' },
                        { id: 2, image: image61, tag: 'LIMITED TIME', title: 'Beverages at Bulk Prices', subtitle: 'Stock up on trending drinks with exclusive partner discounts.', cta: { label: 'View Deals' }, bgColor: '#2D1A0E' },
                      ]}
                      autoPlay
                      interval={6000}
                    />
                  </div>
                )}

                <div className="categories__product" onClick={() => navigate(`/products/${product.id}`)} style={{ cursor: 'pointer' }}>
                  {product.badge && (
                    <>
                      <div
                        className={`categories__product-badge${
                          product.badge === 'sale'
                            ? ' categories__product-badge--sale'
                            : ''
                        }`}
                      />
                      <span className="categories__product-badge-text">
                        {product.badge === 'sale' ? 'Sale' : 'New'}
                      </span>
                    </>
                  )}
                  <div className="categories__product-img-wrap">
                    <img src={image67} alt={product.name} />
                    <button
                      type="button"
                      aria-label={getFavoriteSkuIds().has(String(product.id)) ? 'Remove from favorites' : 'Add to favorites'}
                      aria-pressed={getFavoriteSkuIds().has(String(product.id))}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleFavoriteToggle(product.id);
                      }}
                      style={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        zIndex: 2,
                        width: 30,
                        height: 30,
                        borderRadius: '50%',
                        border: 'none',
                        background: 'rgba(255,255,255,0.9)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        color: getFavoriteSkuIds().has(String(product.id)) ? '#EF4444' : '#9CA3AF',
                      }}
                    >
                      {getFavoriteSkuIds().has(String(product.id)) ? <MdFavorite /> : <MdFavoriteBorder />}
                    </button>
                  </div>
                  <div className="categories__product-info">
                    <p className="categories__product-name">{product.name}</p>
                    <div className="categories__product-prices">
                      <span className="categories__product-price">
                        {product.priceLabel}
                      </span>
                      {product.originalPrice && (
                        <span className="categories__product-original">
                          {product.originalPrice}
                        </span>
                      )}
                    </div>
                    <div className="categories__product-footer">
                      <span
                        className={`categories__product-stock ${
                          product.inStock
                            ? 'categories__product-stock--in'
                            : 'categories__product-stock--out'
                        }`}
                      >
                        {product.inStock ? 'in stock' : 'out of stock'}
                      </span>
                      <AddToCartButton
                        disabled={!product.inStock}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (product.inStock) {
                            addToCart({
                              id: product.id, name: product.name, slug: String(product.id),
                              price: product.price, description: '', shortDescription: '',
                              images: [image67], category: product.category, brand: product.brand,
                              rating: product.rating, reviewCount: 0, inStock: product.inStock,
                              stockCount: 10, tags: [], features: [], sku: '',
                            }, 1);
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>
              </React.Fragment>
            ))}
          </div>

          {/* Loading / Empty State */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--gray-400)' }}>
              <p style={{ fontSize: '1.1rem' }}>Loading products…</p>
            </div>
          ) : (
            paginatedProducts.length === 0 && (
              <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--gray-400)' }}>
                <p style={{ fontSize: '1.1rem', marginBottom: 8 }}>No products found</p>
                <p style={{ fontSize: '0.85rem' }}>Try adjusting your filters.</p>
              </div>
            )
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="categories__pagination">
              <button
                className="categories__page-btn"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => p - 1)}
                aria-label="Previous page"
              >
                <MdArrowBack />
              </button>

              {pageNumbers.map((num) => (
                <button
                  key={num}
                  className={`categories__page-btn${
                    currentPage === num ? ' categories__page-btn--active' : ''
                  }`}
                  onClick={() => setCurrentPage(num)}
                >
                  {num}
                </button>
              ))}

              <button
                className="categories__page-btn"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => p + 1)}
                aria-label="Next page"
              >
                <MdArrowForward />
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CategoriesPage;
